import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {FormattedMessage} from 'react-intl';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';

import {IStore} from 'redux/interface';
import {IMedicationAdherence, IMedicationCheckin, IPatientMedication, MedicationMarkStatus} from 'redux/intervention/interfaces';
import {getPatientMedications, interventionAsyncActions} from 'redux/pageAsyncActions';
import {RoutesUrl} from 'utils/constants/routeConstants';
import {useMedicationAdherence} from 'utils/hooks/useMedicationAdherence';
import {useMedicationCheckins} from 'utils/hooks/useMedicationCheckins';

import Loader from 'components/loader';

import {medicationPercent, summarizeAdherence} from './medicationAdherence';
import {TimeOfDay, countDoses, dosesForDay, groupByTimeOfDay, isDue, yesterdayOf} from './medicationDoses';

import './styles.scss';

const NOW_REFRESH_MS = 60000;
// The engine recomputes adherence from the dose event, which can land just after the mark call returns.
const ADHERENCE_RELOAD_DELAY_MS = 3000;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'});
}

const SectionTitle: React.FC<{timeOfDay: TimeOfDay; isToday: boolean}> = ({timeOfDay, isToday}) => {
  if (!isToday) {
    return (
      <>
        {timeOfDay === 'morning' && <FormattedMessage id="gritx.medications.morning" defaultMessage="Morning"/>}
        {timeOfDay === 'afternoon' && <FormattedMessage id="gritx.medications.afternoon" defaultMessage="Afternoon"/>}
        {timeOfDay === 'evening' && <FormattedMessage id="gritx.medications.evening" defaultMessage="Evening"/>}
      </>
    );
  }

  return (
    <>
      {timeOfDay === 'morning' && (
        <FormattedMessage id="gritx.medications.thisMorning" defaultMessage="This morning"/>
      )}
      {timeOfDay === 'afternoon' && (
        <FormattedMessage id="gritx.medications.thisAfternoon" defaultMessage="This afternoon"/>
      )}
      {timeOfDay === 'evening' && (
        <FormattedMessage id="gritx.medications.thisEvening" defaultMessage="This evening"/>
      )}
    </>
  );
};

const DoseStatus: React.FC<{dose: IMedicationCheckin}> = ({dose}) => {
  const markedTime = dose.markedAt ? formatTime(dose.markedAt) : '';

  switch (dose.status) {
    case 'pending':
      return <FormattedMessage id="gritx.medications.pending" defaultMessage="Pending"/>;
    case 'taken':
      return <FormattedMessage id="gritx.medications.taken" defaultMessage="✓ Taken {time}" values={{time: markedTime}}/>;
    case 'skipped':
      return (
        <FormattedMessage id="gritx.medications.skipped" defaultMessage="Skipped {time}" values={{time: markedTime}}/>
      );
    case 'missed':
      return (
        <FormattedMessage id="gritx.medications.missed" defaultMessage="✗ Missed {time}" values={{time: markedTime}}/>
      );
    default:
      return <>{dose.status}</>;
  }
};

interface IDoseCardProps {
  dose: IMedicationCheckin
  canAct: boolean
  busy: boolean
  onMark: (logId: number, status: 'taken' | 'skipped') => void
}

const DoseCard: React.FC<IDoseCardProps> = ({dose, canAct, busy, onMark}) => (
  <li className={`medication-dose medication-dose--${dose.status}`}>
    <div className="medication-dose__row">
      <div className="medication-dose__main">
        <span className="medication-dose__name">{dose.drugName}</span>
        {dose.dosage && <span className="medication-dose__dosage">{dose.dosage}</span>}
        {dose.programName && <div className="medication-dose__program">{dose.programName}</div>}
      </div>
      <div className="medication-dose__side">
        <span className="medication-dose__time">{formatTime(dose.scheduledAt)}</span>
        <span className={`medication-dose__status medication-dose__status--${dose.status}`}>
          <DoseStatus dose={dose}/>
        </span>
      </div>
    </div>
    {canAct && (
      <div className="medication-dose__actions">
        <button
          type="button"
          className="medication-dose__btn medication-dose__btn--taken"
          disabled={busy}
          onClick={() => onMark(dose.logId, 'taken')}
        >
          <FormattedMessage id="gritx.medications.markTaken" defaultMessage="Mark taken"/>
        </button>
        <button
          type="button"
          className="medication-dose__btn medication-dose__btn--skip"
          disabled={busy}
          onClick={() => onMark(dose.logId, 'skipped')}
        >
          <FormattedMessage id="gritx.medications.skip" defaultMessage="Skip"/>
        </button>
      </div>
    )}
  </li>
);

// Last 7 days, like the admin assignment Medications tab: an overall rate and one bar per medication.
const AdherenceSection: React.FC<{rows: IMedicationAdherence[]}> = ({rows}) => {
  if (rows.length === 0) {
    return null;
  }

  const summary = summarizeAdherence(rows);

  return (
    <section className="medications-page__section">
      <h2 className="medications-page__section-title">
        <FormattedMessage id="gritx.medications.adherence.title" defaultMessage="Adherence"/>
      </h2>
      <div className="medications-adherence">
        <div className="medications-adherence__summary">
          {summary.percent === null
            ? <FormattedMessage id="gritx.medications.adherence.noDoses" defaultMessage="Last 7 days · No doses yet"/>
            : (
              <FormattedMessage
                id="gritx.medications.adherence.overall"
                defaultMessage="Last 7 days · {percent}% overall"
                values={{percent: summary.percent}}
              />
            )}
          {summary.expected > 0 && (
            <span className="medications-adherence__counts">
              <FormattedMessage
                id="gritx.medications.adherence.counts"
                defaultMessage="({taken} taken / {expected} expected)"
                values={{taken: summary.taken, expected: summary.expected}}
              />
            </span>
          )}
        </div>
        <ul className="medications-adherence__list">
          {rows.map((row) => {
            const percent = medicationPercent(row);

            return (
              <li key={row.patientMedicationId} className="medications-adherence__row">
                <span className="medications-adherence__name">{row.drugName}</span>
                <div
                  className="medications-page__bar medications-adherence__bar"
                  role="progressbar"
                  aria-valuenow={percent ?? 0}
                  aria-valuemin={0}
                  aria-valuemax={100}
                >
                  <div className="medications-page__bar-fill" style={{width: `${percent ?? 0}%`}}/>
                </div>
                <span className="medications-adherence__value">
                  {percent === null
                    ? <FormattedMessage id="gritx.medications.adherence.rowNoDoses" defaultMessage="No doses yet"/>
                    : (
                      <FormattedMessage
                        id="gritx.medications.adherence.row"
                        defaultMessage="{percent}% ({taken} / {expected})"
                        values={{percent, taken: row.dosesTaken, expected: row.dosesExpected}}
                      />
                    )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
};

// The patient's prescribed medications (drug, dosage, frequency), shown below the daily check-in.
const PrescribedMedications: React.FC<{medications: IPatientMedication[]}> = ({medications}) => {
  if (medications.length === 0) {
    return null;
  }

  return (
    <section className="medications-page__section">
      <h2 className="medications-page__section-title">
        <FormattedMessage id="gritx.medications.prescribed" defaultMessage="Medications"/>
      </h2>
      <ul className="medications-page__list medications-page__list--prescribed">
        {medications.map((m) => (
          <li key={m.patientMedicationId} className="medication-item">
            <span className="medication-item__name">{m.drugName}</span>
            <span className="medication-item__dosage">{m.dosage}</span>
            {m.frequency && <span className="medication-item__freq">{m.frequency}</span>}
          </li>
        ))}
      </ul>
    </section>
  );
};

export const Medications: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const medications = useSelector((state: IStore) => state.intervention.medications);
  const {doses, loadState, markFailed, busyIds, mark, retry} = useMedicationCheckins();
  const {rows: adherence, reload: reloadAdherence} = useMedicationAdherence();
  const [showYesterday, setShowYesterday] = useState(false);
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    dispatch(interventionAsyncActions[getPatientMedications]());
  }, [dispatch]);

  const markDose = useCallback(async (logId: number, status: MedicationMarkStatus) => {
    await mark(logId, status);
    reloadAdherence();
    window.setTimeout(reloadAdherence, ADHERENCE_RELOAD_DELAY_MS);
  }, [mark, reloadAdherence]);

  // Re-evaluates which pending doses are due, so the buttons appear when a dose's time arrives.
  useEffect(() => {
    const interval = window.setInterval(() => setNow(new Date()), NOW_REFRESH_MS);

    return () => window.clearInterval(interval);
  }, []);

  const day = showYesterday ? yesterdayOf(now) : now;
  const dayDoses = useMemo(() => dosesForDay(doses, day), [doses, day]);
  const sections = useMemo(() => groupByTimeOfDay(dayDoses), [dayDoses]);
  const counts = countDoses(dayDoses);
  const pct = counts.total > 0 ? Math.round((counts.taken / counts.total) * 100) : 0;

  return (
    <div className="medications-page">
      <h1 className="medications-page__title">
        <Link to={RoutesUrl.Dashboard} className="medications-page__back" aria-label="Back to dashboard">‹</Link>
        {showYesterday
          ? <FormattedMessage id="gritx.medications.yesterdayTitle" defaultMessage="Yesterday's medications"/>
          : <FormattedMessage id="gritx.medications.title" defaultMessage="Today's medications"/>}
      </h1>

      {loadState === 'loading' && (
        <div className="medications-page__loader">
          <Loader nested/>
        </div>
      )}

      {loadState === 'error' && (
        <div className="medications-page__message" role="alert">
          <FormattedMessage id="gritx.medications.loadFailed" defaultMessage="Could not load your medications."/>
          <button type="button" className="medications-page__retry" onClick={retry}>
            <FormattedMessage id="gritx.medications.retry" defaultMessage="Retry"/>
          </button>
        </div>
      )}

      {loadState === 'ready' && (
        <>
          {markFailed && (
            <div className="medications-page__message" role="alert">
              <FormattedMessage id="gritx.medications.markFailed" defaultMessage="Could not save. Please try again."/>
            </div>
          )}

          {counts.total > 0 && (
            <div className="medications-page__progress">
              <div className="medications-page__progress-label">
                <FormattedMessage
                  id="gritx.medications.progress"
                  defaultMessage="{taken} of {total} taken"
                  values={{taken: counts.taken, total: counts.total}}
                />
              </div>
              <div
                className="medications-page__bar"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div className="medications-page__bar-fill" style={{width: `${pct}%`}}/>
              </div>
            </div>
          )}

          {counts.total === 0 && (
            <p className="medications-page__empty">
              {showYesterday
                ? <FormattedMessage id="gritx.medications.noneYesterday" defaultMessage="No doses were scheduled yesterday."/>
                : <FormattedMessage id="gritx.medications.noneToday" defaultMessage="No doses scheduled today."/>}
            </p>
          )}

          {sections.map((section) => (
            <section key={section.timeOfDay} className="medications-page__section">
              <h2 className="medications-page__section-title">
                <SectionTitle timeOfDay={section.timeOfDay} isToday={!showYesterday}/>
              </h2>
              <ul className="medications-page__list">
                {section.doses.map((dose) => (
                  <DoseCard
                    key={dose.logId}
                    dose={dose}
                    canAct={!showYesterday && isDue(dose, now)}
                    busy={busyIds.has(dose.logId)}
                    onMark={markDose}
                  />
                ))}
              </ul>
            </section>
          ))}

          <button type="button" className="medications-page__toggle" onClick={() => setShowYesterday((v) => !v)}>
            {showYesterday
              ? <FormattedMessage id="gritx.medications.backToToday" defaultMessage="‹ Today's doses"/>
              : <FormattedMessage id="gritx.medications.yesterdayLink" defaultMessage="‹ Yesterday's doses ›"/>}
          </button>

          <AdherenceSection rows={adherence}/>

          <PrescribedMedications medications={medications}/>
        </>
      )}
    </div>
  );
};
